import type {
  ImageModelCapability,
  RefreshedModelInUse,
  RefreshedProviderEndpoint,
  SaveImageModel,
} from "@nlc/shared-types";
import type { ColumnDef } from "@tanstack/react-table";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Switch } from "@/components/switch";
import { DraggableTable as Table } from "@/components/table";

import { formatMicroUsd } from "../formatUsd";
import {
  acceptProviderPrice,
  CAPABILITY_LABELS,
  type CapabilityName,
  differsFromProviderPrice,
  formatImagePrice,
  formatProviderPrice,
} from "./imageModelDraft";
import { PriceEditor } from "./PriceEditor";

export interface ImageModelRow {
  costMicroUsd: number;
  isChanged: boolean;
  model: SaveImageModel;
  refreshed?: RefreshedModelInUse;
}

interface ImageModelsTableProps {
  isLoading: boolean;
  onChange: (model: SaveImageModel) => void;
  rows: ImageModelRow[];
}

export function ImageModelsTable({
  isLoading,
  onChange,
  rows,
}: ImageModelsTableProps) {
  const columns: ColumnDef<ImageModelRow>[] = [
    {
      cell: ({ row: { original } }) => (
        <div className="flex flex-col">
          <span className="font-medium text-text-primary">
            {original.model.label}
            {original.isChanged && (
              <Badge className="ml-2" size="small" variant="brand">
                Unsaved
              </Badge>
            )}
          </span>
          <span className="text-xs text-text-tertiary">
            {original.model.key}
          </span>
        </div>
      ),
      header: "Model",
      id: "model",
      size: 220,
    },
    {
      accessorFn: (row) => row.model.provider,
      header: "Provider",
      id: "provider",
      size: 90,
    },
    {
      cell: ({ row: { original } }) => <StatusBadges row={original} />,
      header: "Status",
      id: "status",
      size: 140,
    },
    priceColumn("textToImage", onChange),
    priceColumn("edit", onChange),
    {
      cell: ({ row: { original } }) => formatMicroUsd(original.costMicroUsd),
      header: "30-day cost",
      id: "cost",
      size: 110,
    },
    {
      cell: ({ row: { original } }) =>
        original.model.textToImage ? (
          <Switch
            aria-label={`Alpha generation for ${original.model.label}`}
            isSelected={original.model.alpha.generation}
            onChange={(generation) =>
              onChange({
                ...original.model,
                alpha: { ...original.model.alpha, generation },
              })
            }
          />
        ) : (
          <NoEndpoint capability="text-to-image" />
        ),
      header: "Alpha generation",
      id: "generation",
      size: 130,
    },
    {
      cell: ({ row: { original } }) =>
        original.model.edit ? (
          <Switch
            aria-label={`Edit selector for ${original.model.label}`}
            isSelected={original.model.alpha.edit}
            onChange={(edit) =>
              onChange({
                ...original.model,
                alpha: { ...original.model.alpha, edit },
              })
            }
          />
        ) : (
          <NoEndpoint capability="edit" />
        ),
      header: "Edit selector",
      id: "edit-selector",
      size: 120,
    },
  ];

  return (
    <div className="rounded-b-xl bg-bg-primary overflow-auto">
      <Table
        className="rounded-b-xl w-full"
        columns={columns}
        data={rows}
        getRowId={(row) => row.model.key}
        isLoading={isLoading}
      />
    </div>
  );
}

function StatusBadges({ row }: { row: ImageModelRow }) {
  const { model, refreshed } = row;
  const endpointStatuses = new Set(
    [refreshed?.textToImage, refreshed?.edit]
      .filter((endpoint): endpoint is RefreshedProviderEndpoint => !!endpoint)
      .map((endpoint) => endpoint.status),
  );
  const isDeprecated =
    model.status === "deprecated" || endpointStatuses.has("deprecated");
  const isMissing = endpointStatuses.has("missing");

  return (
    <div className="flex flex-wrap gap-1">
      <Badge variant={isDeprecated ? "warning" : "success"}>
        {isDeprecated ? "Deprecated" : "Active"}
      </Badge>
      {isMissing && <Badge variant="error">Missing on fal</Badge>}
    </div>
  );
}

function priceColumn(
  name: CapabilityName,
  onChange: (model: SaveImageModel) => void,
): ColumnDef<ImageModelRow> {
  const label = CAPABILITY_LABELS[name];

  return {
    cell: ({ row: { original } }) => {
      const capability = original.model[name];

      if (!capability) return <span className="text-text-quaternary">-</span>;

      return (
        <PriceCell
          capability={capability}
          label={`${original.model.label} ${label.toLowerCase()}`}
          onChange={(next) => onChange({ ...original.model, [name]: next })}
          refreshed={original.refreshed?.[name] ?? null}
        />
      );
    },
    header: `${label} price`,
    id: `${name}-price`,
    size: 260,
  };
}

function PriceCell({
  capability,
  label,
  onChange,
  refreshed,
}: {
  capability: ImageModelCapability;
  label: string;
  onChange: (capability: ImageModelCapability) => void;
  refreshed: null | RefreshedProviderEndpoint;
}) {
  const { price } = capability;
  const providerPrice = refreshed?.providerPrice;
  const hasDifference =
    !!providerPrice && differsFromProviderPrice(price, providerPrice);

  return (
    <div className="flex flex-col gap-1 py-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-text-primary">{formatImagePrice(price)}</span>
        {!price.verified && (
          <Badge size="small" variant="warning">
            est.
          </Badge>
        )}
        <PriceEditor
          label={label}
          onSave={(next) => onChange({ ...capability, price: next })}
          price={price}
        />
      </div>
      {providerPrice && (
        <div
          className={
            hasDifference
              ? "flex items-center gap-2 rounded-sm bg-utility-warning-50 px-2 py-1 text-xs text-utility-warning-700"
              : "text-xs text-text-tertiary"
          }
        >
          <span>fal: {formatProviderPrice(providerPrice)}</span>
          {hasDifference && refreshed && (
            <Button
              aria-label={`Accept fal price for ${label}`}
              color="gray"
              onPress={() =>
                onChange(acceptProviderPrice(capability, refreshed))
              }
              size="sm"
              variant="secondary"
            >
              Accept fal price
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function NoEndpoint({ capability }: { capability: string }) {
  return (
    <span className="text-xs text-text-tertiary">No {capability} endpoint</span>
  );
}
